import { Stack, Text } from "@mantine/core";

interface PasskeyErrorMessageProps {
	error: Error;
}

/*
 * Amplify's PasskeyError messages are written for developers, so known passkey
 * error names get user-facing guidance. The provider's own text (from the
 * browser or native plugin) is shown as details because it is often the only
 * specific explanation available.
 */
export function PasskeyErrorMessage({ error }: PasskeyErrorMessageProps) {
	const guidance = getPasskeyErrorGuidance(error.name);
	if (guidance === undefined) {
		return <Text size="sm">{error.message}</Text>;
	}
	const detail = getUnderlyingErrorText(error);
	return (
		<Stack gap={4}>
			<Text size="sm">{guidance}</Text>
			{detail !== undefined && (
				<Text size="xs" c="dimmed">
					Details: {detail}
				</Text>
			)}
		</Stack>
	);
}

function getPasskeyErrorGuidance(name: string): string | undefined {
	if (name === unknownErrorName) {
		return unknownPasskeyErrorMessage;
	}
	return passkeyErrorGuidanceByName.get(name);
}

function getUnderlyingErrorText(error: Error): string | undefined {
	if (!("underlyingError" in error)) {
		return undefined;
	}
	const underlyingError = error.underlyingError;
	if (!(underlyingError instanceof Error)) {
		return undefined;
	}
	const text = underlyingError.message.trim();
	return text === "" ? undefined : text;
}

// AmplifyErrorCode.Unknown, which Amplify uses for passkey errors it cannot classify
const unknownErrorName = "Unknown";

const unknownPasskeyErrorMessage =
	"Something went wrong creating the passkey. Make sure this device has a screen lock and a password manager (such as Google Password Manager or iCloud Keychain) turned on, then try again.";

const passkeyErrorGuidanceByName = new Map<string, string>([
	[
		"PasskeyAlreadyExists",
		"A passkey for this account is already saved on this device or in your password manager. Sign in with that passkey, or delete it from your password manager and from the list above before adding a new one.",
	],
	[
		"PasskeyRegistrationCanceled",
		"Passkey setup was canceled or timed out. Try again, and make sure this device has a screen lock set up.",
	],
	["PasskeyOperationAborted", "Passkey setup was interrupted. Try again."],
	[
		"RelyingPartyMismatch",
		"This app is not allowed to create passkeys for this site. Contact support.",
	],
	[
		"PasskeyNotSupported",
		"Passkeys are not supported on this device or browser.",
	],
	["PasskeyRegistrationFailed", unknownPasskeyErrorMessage],
]);
